import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CardActionType, CardPrint, CardPrintConfig, CardPrintConfigExpirationStringType, CardStatus, CardSubType, CardType, ICard, OrganisationConfiguration, Sex } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ConfigAllService } from 'src/app/global/services/config-all/config-all.abstract.service';
import { ShortDatePipe } from 'src/app/global/pipes/shortDate/short-date.pipe';
import { GetUserActiveSubscriptionsResponse, PatchOrganisationConfiguration } from 'src/app/global/services/config-all/config-all.model';
import { Organisation } from 'src/app/global/global.model';
import { CardPrintService } from 'src/app/global/utils/card-print/cart-print.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { organisationConfigurationSelector, languageSelector, organisationSelector } from 'src/app/global/store/global.selectors';
import { GetOrganisationConfigurationAction, SetOrganisationConfigurationAction } from 'src/app/global/store/global.actions';
import { ofType, Actions } from '@ngrx/effects';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';

@Component({
    selector: 'app-config-print-cards',
    templateUrl: './config-print-cards.component.html',
    styleUrls: ['./config-print-cards.component.scss'],
})
export class ConfigPrintCardsComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();
    loading = false;
    cardExample: ICard;
    subscriptions: GetUserActiveSubscriptionsResponse[];
    cardPDF: any;
    orgConfig: OrganisationConfiguration;
    allCoreConfig: any;
    organisation: Organisation;
    selectedCodeStyle: string;
    cardPrintCopy: CardPrint;
    displayPreview: boolean = false;
    processingSubmit: boolean = false;
    formLoading = true;

    public codeStyleOptions = [];
    private codeStyleHasBeenSelectedAtLeastOnce = false;
    public expirationDateStringStyleOptions = CardPrintConfigExpirationStringType;

    public qrcodeOpt = {
        elementType: NgxQrcodeElementTypes.URL,
        correctionLevel: NgxQrcodeErrorCorrectionLevels.HIGH,
    };

    public printTemplateForm: FormGroup;
    public selectedCodeStyleForm: FormGroup;
    public exampleCardForm: FormGroup;

    get qrCodeForImage() {
        if (this.orgConfig?.name.toUpperCase() === 'BROSSARD') {
            return `QBD${this.cardExample?.code}`;
        }
        return this.cardExample?.code;
    }

    constructor(
        public store: Store<State>,
        private actions$: Actions,
        private messageService: MessageService,
        public translate: TranslateService,
        private cardPrintService: CardPrintService,
        private configAllService: ConfigAllService,
        private fb: FormBuilder,
        private shortDatePipe: ShortDatePipe,
    ) {}

    ngOnInit() {
        this.selectedCodeStyleForm = this.fb.group({
            selectedCodeStyle: new FormControl(null),
        });

        this.printTemplateForm = this.fb.group(
            {
                residency: this.fb.group({
                    x: new FormControl('', [this.requiredIfValidator(() => this.residencyDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.residencyDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                photo: this.fb.group({
                    height: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    width: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    x: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                name: this.fb.group({
                    x: new FormControl('', [this.requiredIfValidator(() => this.nameDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.nameDisplay.value), Validators.pattern('^[0-9]*$')]),
                    maxWidth: new FormControl('', [this.requiredIfValidator(() => this.nameDisplay.value), Validators.pattern('^[0-9]*$')]),
                    fontSize: new FormControl('', [this.requiredIfValidator(() => this.nameDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                address: this.fb.group({
                    x: new FormControl('', [this.requiredIfValidator(() => this.addressDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.addressDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                scanCode: this.fb.group({
                    height: new FormControl('', [this.requiredIfValidator(() => this.scanCodeDisplay.value), Validators.pattern('^[0-9]*$')]),
                    width: new FormControl('', [this.requiredIfValidator(() => this.scanCodeDisplay.value), Validators.pattern('^[0-9]*$')]),
                    x: new FormControl('', [this.requiredIfValidator(() => this.scanCodeDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.scanCodeDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                cardCode: this.fb.group({
                    x: new FormControl('', [this.requiredIfValidator(() => this.cardCodeDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.cardCodeDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                cardType: this.fb.group({
                    x: new FormControl('', [this.requiredIfValidator(() => this.cardTypeDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.cardTypeDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                expiration: this.fb.group({
                    x: new FormControl('', [this.requiredIfValidator(() => this.expirationDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.expirationDisplay.value), Validators.pattern('^[0-9]*$')]),
                    stringType: new FormControl(CardPrintConfigExpirationStringType.SHORT, [this.requiredIfValidator(() => this.expirationDisplay.value)]),
                    display: new FormControl(true, [Validators.required]),
                    rectangle: this.fb.group({
                        x: new FormControl('', [this.requiredIfValidator(() => this.expirationDisplay.value), Validators.pattern('^[0-9]*$')]),
                        y: new FormControl('', [this.requiredIfValidator(() => this.expirationDisplay.value), Validators.pattern('^[0-9]*$')]),
                        height: new FormControl('', [this.requiredIfValidator(() => this.expirationDisplay.value), Validators.pattern('^[0-9]*$')]),
                        width: new FormControl('', [this.requiredIfValidator(() => this.expirationDisplay.value), Validators.pattern('^[0-9]*$')]),
                        display: new FormControl(true, [Validators.required]),
                    }),
                }),
                orgLogo: this.fb.group({
                    height: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    width: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    x: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                bcitiLogo: this.fb.group({
                    height: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    width: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    x: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    y: new FormControl('', [this.requiredIfValidator(() => this.photoDisplay.value), Validators.pattern('^[0-9]*$')]),
                    display: new FormControl(true, [Validators.required]),
                }),
                outlineCuttingHelp: new FormControl(true, [Validators.required]),
            },
            {
                updateOn: 'change',
            },
        );

        this.exampleCardForm = this.fb.group(
            {
                firstName: new FormControl('Lorem', [Validators.required]),
                lastName: new FormControl('Ipsum', [Validators.required]),
            },
            {
                updateOn: 'change',
            },
        );

        // update validators on checkbox change for each display control
        this.residencyDisplay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.residencyPositionX.updateValueAndValidity();
            this.residencyPositionY.updateValueAndValidity();
        });

        this.photoDisplay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.photoPositionX.updateValueAndValidity();
            this.photoPositionY.updateValueAndValidity();
            this.photoWidth.updateValueAndValidity();
            this.photoHeight.updateValueAndValidity();
        });

        this.nameDisplay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.namePositionX.updateValueAndValidity();
            this.namePositionY.updateValueAndValidity();
            this.nameMaxWidth.updateValueAndValidity();
            this.nameFontSize.updateValueAndValidity();
        });

        this.addressDisplay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.addressPositionX.updateValueAndValidity();
            this.addressPositionY.updateValueAndValidity();
        });

        this.scanCodeDisplay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.scanCodePositionX.updateValueAndValidity();
            this.scanCodePositionY.updateValueAndValidity();
            this.scanCodeWidth.updateValueAndValidity();
            this.scanCodeHeight.updateValueAndValidity();
        });

        this.cardCodeDisplay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.cardCodeX.updateValueAndValidity();
            this.cardCodeY.updateValueAndValidity();
        });

        this.cardTypeDisplay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.cardTypePositionX.updateValueAndValidity();
            this.cardTypePositionY.updateValueAndValidity();
        });

        this.expirationDisplay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.expirationDateX.updateValueAndValidity();
            this.expirationDateY.updateValueAndValidity();
            this.rectangleX.updateValueAndValidity();
            this.rectangleY.updateValueAndValidity();
            this.rectangleWidth.updateValueAndValidity();
            this.rectangleHeight.updateValueAndValidity();
        });

        this.exampleFirstName.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                map(() => this.updateCardExample()),
            )
            .subscribe();

        this.exampleLastName.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                map(() => this.updateCardExample()),
            )
            .subscribe();

        this.formLoading = false;

        combineLatest([this.store.select(organisationConfigurationSelector), this.store.select(languageSelector), this.configAllService.getAllCoreConfig(), this.store.select(organisationSelector)])
            .pipe(
                map(([orgConfig, lang, allCoreConfig, org]) => {
                    this.organisation = org;
                    this.allCoreConfig = allCoreConfig;
                    this.translate.use(lang);
                    this.orgConfig = orgConfig as OrganisationConfiguration;
                    this.cardPrintCopy = orgConfig.cardPrint;
                    this.initCardExample();
                    this.initCodeStyleOptions();
                }),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Used in a 'new FormControl()' declaration.
    // formControl is the reference to context object
    // ex: new FormControl("", [this.requiredIfValidator(() => this.nameDisplay.value), Validators.pattern("^[0-9]*$")])
    private requiredIfValidator(predicate) {
        return (formControl) => {
            if (!formControl.parent) {
                return null;
            }
            if (predicate()) {
                return Validators.required(formControl);
            }
            return null;
        };
    }

    private initCodeStyleOptions() {
        this.codeStyleOptions = [
            {
                id: 1,
                name: this.translate.instant('pages.config-all.config-cards.config-print-cards.code-options.qrCode'),
                value: 'QRCODE',
            },
            {
                id: 2,
                name: this.translate.instant('pages.config-all.config-cards.config-print-cards.code-options.barCode'),
                value: 'BARCODE',
            },
        ];
    }

    public async refreshPDF() {
        if (this.printTemplateForm.valid) {
            this.loading = true;
            this.displayPreview = true;
            this.saveCurrentConfig();
            if (this.selectedCodeStyle === 'QRCODE') {
                this.cardPDF = (
                    await this.cardPrintService.generateQrCodePdf(
                        this.cardExample,
                        this.organisation,
                        this.orgConfig,
                        this.subscriptions,
                        this.translate,
                        this.cardPrintCopy.qrCode,
                        true,
                        this.shortDatePipe,
                    )
                ).url;
            } else {
                this.cardPDF = (
                    await this.cardPrintService.generateBarCodePdf(
                        this.cardExample,
                        this.organisation,
                        this.orgConfig,
                        this.subscriptions,
                        this.translate,
                        this.cardPrintCopy.barCode,
                        true,
                        this.shortDatePipe,
                    )
                ).url;
            }
            this.loading = false;
        }
    }

    public submit() {
        this.processingSubmit = true;
        this.saveCurrentConfig();
        const payload: PatchOrganisationConfiguration = {
            group: 'cardPrint',
            data: this.cardPrintCopy,
        };

        this.configAllService
            .patchOrganisationConfiguration(payload, this.organisation.tag)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.orgConfig = response;
                    this.cardPrintCopy = this.orgConfig.cardPrint;
                    this.processingSubmit = false;
                    this.messageService.success(this.translate.instant('pages.config-all.config-cards.messages.success'));
                    this.store.dispatch(GetOrganisationConfigurationAction());
                    return this.actions$.pipe(
                        ofType(SetOrganisationConfigurationAction),
                        map(({ configuration }) => configuration),
                    );
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(this.translate.instant('pages.config-all.config-cards.messages.error'));
                    console.log(error);
                },
            );
    }

    public resetAllValues() {
        const defaultTemplate: CardPrintConfig = this.selectedCodeStyle === 'QRCODE' ? this.allCoreConfig.defaultCardPrint.qrCode : this.allCoreConfig.defaultCardPrint.barCode;

        this.printTemplateForm.setValue({
            name: {
                x: defaultTemplate.name.x,
                y: defaultTemplate.name.y,
                fontSize: defaultTemplate.name.fontSize,
                maxWidth: defaultTemplate.name.maxWidth,
                display: defaultTemplate.name.display,
            },
            photo: {
                x: defaultTemplate.photo.x,
                y: defaultTemplate.photo.y,
                height: defaultTemplate.photo.height,
                width: defaultTemplate.photo.width,
                display: defaultTemplate.photo.display,
            },
            residency: {
                x: defaultTemplate.residency.x,
                y: defaultTemplate.residency.y,
                display: defaultTemplate.residency.display,
            },
            address: {
                x: defaultTemplate.address.x,
                y: defaultTemplate.address.y,
                display: defaultTemplate.address.display,
            },
            scanCode: {
                x: defaultTemplate.scanCode.x,
                y: defaultTemplate.scanCode.y,
                height: defaultTemplate.scanCode.height,
                width: defaultTemplate.scanCode.width,
                display: defaultTemplate.scanCode.display,
            },
            cardCode: {
                x: defaultTemplate.cardCode.x,
                y: defaultTemplate.cardCode.y,
                display: defaultTemplate.cardCode.display,
            },
            cardType: {
                x: defaultTemplate.cardType.x,
                y: defaultTemplate.cardType.y,
                display: defaultTemplate.cardType.display,
            },
            expiration: {
                x: defaultTemplate.expiration.x,
                y: defaultTemplate.expiration.y,
                display: defaultTemplate.expiration.display,
                stringType: defaultTemplate.expiration.stringType || CardPrintConfigExpirationStringType.SHORT,
                rectangle: {
                    x: defaultTemplate.expiration.rectangle.x,
                    y: defaultTemplate.expiration.rectangle.y,
                    height: defaultTemplate.expiration.rectangle.height,
                    width: defaultTemplate.expiration.rectangle.width,
                    display: defaultTemplate.expiration.display,
                },
            },
            orgLogo: {
                x: defaultTemplate.orgLogo.x,
                y: defaultTemplate.orgLogo.y,
                display: defaultTemplate.orgLogo.display,
                height: defaultTemplate.orgLogo.height,
                width: defaultTemplate.orgLogo.width,
            },
            bcitiLogo: {
                x: defaultTemplate.bcitiLogo.x,
                y: defaultTemplate.bcitiLogo.y,
                display: defaultTemplate.bcitiLogo.display,
                height: defaultTemplate.bcitiLogo.height,
                width: defaultTemplate.bcitiLogo.width,
            },
            outlineCuttingHelp: defaultTemplate.outlineCuttingHelp,
        });
    }

    private initFormValues(orgTemplate: CardPrintConfig, defaultTemplate: CardPrintConfig) {
        this.printTemplateForm.setValue({
            name: {
                x: orgTemplate?.name?.x || defaultTemplate?.name?.x,
                y: orgTemplate?.name?.y || defaultTemplate?.name?.y,
                maxWidth: orgTemplate?.name?.maxWidth || defaultTemplate?.name?.maxWidth || 10,
                fontSize: orgTemplate?.name?.fontSize || defaultTemplate.name.fontSize || 12,
                display: orgTemplate?.name?.display ?? defaultTemplate?.name?.display,
            },
            photo: {
                x: orgTemplate?.photo?.x || defaultTemplate?.photo?.x,
                y: orgTemplate?.photo?.y || defaultTemplate?.photo?.y,
                height: orgTemplate?.photo?.height || defaultTemplate?.photo?.height,
                width: orgTemplate?.photo?.width || defaultTemplate?.photo?.width,
                display: orgTemplate?.photo?.display ?? defaultTemplate?.photo?.display,
            },
            residency: {
                x: orgTemplate?.residency?.x || defaultTemplate?.residency?.x,
                y: orgTemplate?.residency?.y || defaultTemplate?.residency?.y,
                display: orgTemplate?.residency?.display ?? defaultTemplate?.residency?.display,
            },
            address: {
                x: orgTemplate?.address?.x || defaultTemplate?.address?.x,
                y: orgTemplate?.address?.y || defaultTemplate?.address?.y,
                display: orgTemplate?.address?.display ?? defaultTemplate?.address?.display,
            },
            scanCode: {
                x: orgTemplate?.scanCode?.x || defaultTemplate?.scanCode?.x,
                y: orgTemplate?.scanCode?.y || defaultTemplate?.scanCode?.y,
                height: orgTemplate?.scanCode?.height || defaultTemplate?.scanCode?.height,
                width: orgTemplate?.scanCode?.width || defaultTemplate?.scanCode?.width,
                display: orgTemplate?.scanCode?.display ?? defaultTemplate?.scanCode?.display,
            },
            cardCode: {
                x: orgTemplate?.cardCode?.x || defaultTemplate?.cardCode?.x,
                y: orgTemplate?.cardCode?.y || defaultTemplate?.cardCode?.y,
                display: orgTemplate?.cardCode?.display ?? defaultTemplate?.cardCode?.display,
            },
            cardType: {
                x: orgTemplate?.cardType?.x || defaultTemplate?.cardType?.x,
                y: orgTemplate?.cardType?.y || defaultTemplate?.cardType?.y,
                display: orgTemplate?.cardType.display ?? defaultTemplate?.cardType?.display,
            },
            expiration: {
                x: orgTemplate?.expiration?.x || defaultTemplate?.expiration?.x,
                y: orgTemplate?.expiration?.y || defaultTemplate?.expiration?.y,
                display: orgTemplate?.expiration?.display ?? defaultTemplate?.expiration?.display,
                stringType: orgTemplate?.expiration?.stringType || defaultTemplate?.expiration?.stringType,
                rectangle: {
                    x: orgTemplate?.expiration?.rectangle?.x || defaultTemplate?.expiration?.rectangle?.x,
                    y: orgTemplate?.expiration?.rectangle?.y || defaultTemplate?.expiration?.rectangle?.y,
                    height: orgTemplate?.expiration?.rectangle?.height || defaultTemplate?.expiration?.rectangle?.height,
                    width: orgTemplate?.expiration?.rectangle?.width || defaultTemplate?.expiration?.rectangle?.width,
                    display: orgTemplate?.expiration?.display ?? defaultTemplate?.expiration?.display,
                },
            },
            orgLogo: {
                x: orgTemplate?.orgLogo?.x || defaultTemplate?.orgLogo?.x,
                y: orgTemplate?.orgLogo?.y || defaultTemplate?.orgLogo?.y,
                height: orgTemplate?.orgLogo?.height || defaultTemplate?.orgLogo?.height || 0,
                width: orgTemplate?.orgLogo?.width || defaultTemplate?.orgLogo?.width || 0,
                display: orgTemplate?.orgLogo?.display ?? defaultTemplate?.orgLogo?.display,
            },
            bcitiLogo: {
                x: orgTemplate?.bcitiLogo?.x || defaultTemplate?.bcitiLogo?.x,
                y: orgTemplate?.bcitiLogo?.y || defaultTemplate?.bcitiLogo?.y,
                height: orgTemplate?.bcitiLogo?.height || defaultTemplate?.bcitiLogo?.height || 0,
                width: orgTemplate?.bcitiLogo?.width || defaultTemplate?.bcitiLogo?.width || 0,
                display: orgTemplate?.bcitiLogo?.display ?? defaultTemplate?.bcitiLogo?.display,
            },
            outlineCuttingHelp: orgTemplate?.outlineCuttingHelp ?? defaultTemplate?.outlineCuttingHelp ?? true,
        });
    }

    private initCardExample() {
        this.cardExample = {
            actionType: CardActionType.SUBSCRIBE,
            _id: '5ed51cac6a947b49ef2701c6',
            cardType: CardType.CITIZEN,
            subType: CardSubType.RESIDENT,
            number: '4567890',
            code: 'A04456789004',
            address: {
                street: 'Rue Principale',
                postCode: 'V9W5C1',
                number: '709',
                city: this.organisation.name,
                province: 'QC',
                latitude: 45.730295,
                longitude: -76.603025,
                streetName: 'RIVER BC',
                direction: '',
                unit: '',
                typeStreet: '',
            },
            expirationDate: new Date('2021-02-02T17:00:00.00Z'),
            subscriptionDate: new Date(),
            status: CardStatus.ACTIVE,
            lastName: this.exampleCardForm.get('lastName').value,
            firstName: this.exampleCardForm.get('firstName').value,
            photo: 'https://bcitilite.blob.core.windows.net/prod/default-avatar.png',
            created: new Date('2020-09-22T00:00:00'),
            lastModified: new Date('2020-09-22T00:00:00'),
            proofOfIdentity: ['asd'],
            renewal: null,
            birthDate: new Date('1990-09-22T00:00:00'),
            sex: Sex.Male,
        };
    }

    private updateCardExample(): void {
        this.cardExample = {
            ...this.cardExample,
            firstName: this.exampleFirstName.value,
            lastName: this.exampleLastName.value,
        };
    }

    private saveCurrentConfig() {
        this.printTemplateForm.markAsPristine();
        const template: Partial<CardPrintConfig> = {
            residency: {
                x: +this.residencyPositionX.value,
                y: +this.residencyPositionY.value,
                display: this.residencyDisplay.value,
            },
            photo: {
                x: +this.photoPositionX.value,
                y: +this.photoPositionY.value,
                height: +this.photoHeight.value,
                width: +this.photoWidth.value,
                display: this.photoDisplay.value,
            },
            name: {
                x: +this.namePositionX.value,
                y: +this.namePositionY.value,
                maxWidth: +this.nameMaxWidth.value,
                fontSize: +this.nameFontSize.value,
                display: this.nameDisplay.value,
            },
            address: {
                x: +this.addressPositionX.value,
                y: +this.addressPositionY.value,
                display: this.addressDisplay.value,
            },
            scanCode: {
                x: +this.scanCodePositionX.value,
                y: +this.scanCodePositionY.value,
                height: +this.scanCodeHeight.value,
                width: +this.scanCodeWidth.value,
                display: this.scanCodeDisplay.value,
            },
            cardCode: {
                x: +this.cardCodeX.value,
                y: +this.cardCodeY.value,
                display: this.cardCodeDisplay.value,
            },
            cardType: {
                x: +this.cardTypePositionX.value,
                y: +this.cardTypePositionY.value,
                display: this.cardTypeDisplay.value,
            },
            expiration: {
                x: +this.expirationDateX.value,
                y: +this.expirationDateY.value,
                display: this.expirationDisplay.value,
                stringType: this.expirationStringType.value,
                rectangle: {
                    x: +this.rectangleX.value,
                    y: +this.rectangleY.value,
                    height: +this.rectangleHeight.value,
                    width: +this.rectangleWidth.value,
                    display: this.expirationDisplay.value,
                },
            },
            orgLogo: {
                x: +this.orgLogoX.value,
                y: +this.orgLogoY.value,
                display: this.orgLogoDisplay.value,
                height: +this.orgLogoHeight.value,
                width: +this.orgLogoWidth.value,
            },
            bcitiLogo: {
                x: +this.bcitiLogoX.value,
                y: +this.bcitiLogoY.value,
                display: this.bcitiLogoDisplay.value,
                height: +this.bcitiLogoHeight.value,
                width: +this.bcitiLogoWidth.value,
            },
            outlineCuttingHelp: this.outlineCuttingHelpDisplay.value,
        };

        if (this.selectedCodeStyle === 'QRCODE') {
            this.cardPrintCopy = {
                qrCode: template as CardPrintConfig,
                barCode: this.cardPrintCopy?.barCode,
                includeCardType: this.cardPrintCopy?.includeCardType,
            };
        } else {
            this.cardPrintCopy = {
                qrCode: this.cardPrintCopy?.qrCode,
                barCode: template as CardPrintConfig,
                includeCardType: this.cardPrintCopy?.includeCardType,
            };
        }
    }

    public selectedCodeStyleChanged(value: any) {
        // TODO: Refactor into two forms for simpler data mangement
        // if a codeStyle is selected
        // Update current config so we dont lose unsaved data
        if (!value?.value) {
            return;
        }
        if (this.printTemplateForm.valid && this.codeStyleHasBeenSelectedAtLeastOnce) {
            this.saveCurrentConfig();
        }
        this.selectedCodeStyle = value.value;
        this.codeStyleHasBeenSelectedAtLeastOnce = true;
        if (this.selectedCodeStyle === 'QRCODE') {
            this.initFormValues(this.cardPrintCopy?.qrCode || this.orgConfig.cardPrint.qrCode, this.allCoreConfig.defaultCardPrint.qrCode);
        } else {
            this.initFormValues(this.cardPrintCopy?.barCode || this.orgConfig.cardPrint.barCode, this.allCoreConfig.defaultCardPrint.barCode);
        }
        if (this.displayPreview) {
            this.refreshPDF();
        }
    }

    /**
     * GETTERS
     */

    // Residency
    get residencyPositionX() {
        return this.printTemplateForm.get('residency.x');
    }

    get residencyPositionY() {
        return this.printTemplateForm.get('residency.y');
    }

    get residencyDisplay() {
        return this.printTemplateForm.get('residency.display');
    }

    // Photo
    get photoPositionX() {
        return this.printTemplateForm.get('photo.x');
    }

    get photoPositionY() {
        return this.printTemplateForm.get('photo.y');
    }

    get photoWidth() {
        return this.printTemplateForm.get('photo.width');
    }

    get photoHeight() {
        return this.printTemplateForm.get('photo.height');
    }

    get photoDisplay() {
        return this.printTemplateForm.get('photo.display');
    }

    // Name
    get namePositionX() {
        return this.printTemplateForm.get('name.x');
    }

    get namePositionY() {
        return this.printTemplateForm.get('name.y');
    }

    get nameMaxWidth() {
        return this.printTemplateForm.get('name.maxWidth');
    }

    get nameFontSize() {
        return this.printTemplateForm.get('name.fontSize');
    }

    get nameDisplay() {
        return this.printTemplateForm.get('name.display');
    }

    // Address
    get addressPositionX() {
        return this.printTemplateForm.get('address.x');
    }

    get addressPositionY() {
        return this.printTemplateForm.get('address.y');
    }

    get addressDisplay() {
        return this.printTemplateForm.get('address.display');
    }

    // Scan code
    get scanCodePositionX() {
        return this.printTemplateForm.get('scanCode.x');
    }

    get scanCodePositionY() {
        return this.printTemplateForm.get('scanCode.y');
    }

    get scanCodeWidth() {
        return this.printTemplateForm.get('scanCode.width');
    }

    get scanCodeHeight() {
        return this.printTemplateForm.get('scanCode.height');
    }

    get scanCodeDisplay() {
        return this.printTemplateForm.get('scanCode.display');
    }

    // Card code
    get cardCodeX() {
        return this.printTemplateForm.get('cardCode.x');
    }

    get cardCodeY() {
        return this.printTemplateForm.get('cardCode.y');
    }

    get cardCodeDisplay() {
        return this.printTemplateForm.get('cardCode.display');
    }

    // Card type
    get cardTypePositionX() {
        return this.printTemplateForm.get('cardType.x');
    }

    get cardTypePositionY() {
        return this.printTemplateForm.get('cardType.y');
    }

    get cardTypeDisplay() {
        return this.printTemplateForm.get('cardType.display');
    }

    // Expiration date
    get rectangleX() {
        return this.printTemplateForm.get('expiration.rectangle.x');
    }

    get rectangleY() {
        return this.printTemplateForm.get('expiration.rectangle.y');
    }

    get rectangleHeight() {
        return this.printTemplateForm.get('expiration.rectangle.height');
    }

    get rectangleWidth() {
        return this.printTemplateForm.get('expiration.rectangle.width');
    }

    get expirationRectangleDisplay() {
        return this.printTemplateForm.get('expiration.rectangle.display');
    }

    get expirationDateX() {
        return this.printTemplateForm.get('expiration.x');
    }

    get expirationDateY() {
        return this.printTemplateForm.get('expiration.y');
    }

    get expirationDisplay() {
        return this.printTemplateForm.get('expiration.display');
    }

    get expirationStringType() {
        return this.printTemplateForm.get('expiration.stringType');
    }

    // Org Logo
    get orgLogoX() {
        return this.printTemplateForm.get('orgLogo.x');
    }

    get orgLogoY() {
        return this.printTemplateForm.get('orgLogo.y');
    }

    get orgLogoDisplay() {
        return this.printTemplateForm.get('orgLogo.display');
    }

    get orgLogoWidth() {
        return this.printTemplateForm.get('orgLogo.width');
    }

    get orgLogoHeight() {
        return this.printTemplateForm.get('orgLogo.height');
    }

    // Bciti Logo
    get bcitiLogoX() {
        return this.printTemplateForm.get('bcitiLogo.x');
    }

    get bcitiLogoY() {
        return this.printTemplateForm.get('bcitiLogo.y');
    }

    get bcitiLogoDisplay() {
        return this.printTemplateForm.get('bcitiLogo.display');
    }

    get bcitiLogoWidth() {
        return this.printTemplateForm.get('bcitiLogo.width');
    }

    get bcitiLogoHeight() {
        return this.printTemplateForm.get('bcitiLogo.height');
    }

    // outline cuttingHelp
    get outlineCuttingHelpDisplay() {
        return this.printTemplateForm.get('outlineCuttingHelp');
    }

    // ** EXAMPLE CARD FORM **
    get exampleFirstName() {
        return this.exampleCardForm.get('firstName');
    }

    get exampleLastName() {
        return this.exampleCardForm.get('lastName');
    }
}
